import React, { useState, useEffect, useCallback } from 'react';
import { householdAPI } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { FormGroup } from '../common/FormComponents';
import TextInput from '../common/TextInput';

/**
 * HouseholdSettings - Settings tab for managers
 */
function HouseholdSettings() {
  const { success, error: showError } = useToast();
  const [settings, setSettings] = useState({
    householdName: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const loadHousehold = useCallback(async () => {
    setLoading(true);
    try {
      const response = await householdAPI.getCurrent();
      const household = response.data;
      
      setSettings({
        householdName: household.name || ''
      });
    } catch (error) {
      console.error('Failed to load household:', error);
      showError('Failed to load household settings');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadHousehold();
  }, [loadHousehold]);

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await householdAPI.update({
        name: settings.householdName
      });

      const household = JSON.parse(localStorage.getItem('household') || '{}');
      household.name = settings.householdName;
      localStorage.setItem('household', JSON.stringify(household));

      success('Household settings updated successfully!');
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to update household settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteHousehold = async () => {
    if (deleteConfirmText !== settings.householdName) {
      showError('Please type the household name exactly to confirm deletion');
      return;
    }

    setSaving(true);
    try {
      await householdAPI.delete();
      success('Household deleted successfully');
      // Clear local storage and reload
      localStorage.removeItem('household');
      window.location.reload();
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to delete household');
    } finally {
      setSaving(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <div className="household-section">
        <h2>Household Settings</h2>
        <div className="loading-state">Loading household settings...</div>
      </div>
    );
  }

  return (
    <div className="household-section">
      <h2>Household Settings</h2>
      <p className="section-description">Configure household-wide settings</p>
      
      <form onSubmit={handleSubmit} className="household-settings-form">
        {/* Basic Information */}
        <div className="settings-group">
          <h3>Basic Information</h3>
          <p className="section-hint">Update your household name</p>
          
          <FormGroup 
            label="Household Name" 
            htmlFor="householdName"
            hint="This name is visible to all household members"
          >
            <TextInput
              id="householdName"
              value={settings.householdName}
              onChange={(e) => handleChange('householdName', e.target.value)}
              required
              disabled={saving}
              placeholder="Enter household name"
            />
          </FormGroup>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>

      {/* Danger Zone */}
      <div className="settings-group danger-zone" style={{ marginTop: 'var(--spacing-2xl)' }}>
        <h3>⚠️ Danger Zone</h3>
        <p className="section-hint">Irreversible actions that affect your household</p>
        
        <div className="danger-action-item">
          <div className="action-info">
            <h4>Delete Household</h4>
            <p>Permanently delete this household and remove all members. All bills, payment history, payment accounts, and categories will be deleted.</p>
          </div>
          <button 
            type="button"
            className="btn-danger"
            onClick={() => setShowDeleteModal(true)}
            disabled={saving}
          >
            Delete Household
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-backdrop" onClick={() => !saving && setShowDeleteModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>⚠️ Delete Household</h3>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)} disabled={saving}>×</button>
            </div>
            <div className="modal-body">
              <div className="danger-warning" style={{ 
                backgroundColor: 'rgba(239, 68, 68, 0.1)', 
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: 'var(--border-radius-sm)',
                padding: 'var(--spacing-md)',
                marginBottom: 'var(--spacing-md)'
              }}>
                <p style={{ color: '#ef4444', fontWeight: 600, marginBottom: 'var(--spacing-sm)' }}>
                  This action cannot be undone!
                </p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  Deleting this household will:
                </p>
                <ul style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginLeft: 'var(--spacing-lg)', marginTop: 'var(--spacing-sm)' }}>
                  <li>Remove all members from the household</li>
                  <li>Delete all household payment accounts</li>
                  <li>Delete all household bill categories</li>
                  <li>Delete all household bills and payment history</li>
                </ul>
              </div>
              
              <FormGroup 
                label={`Type "${settings.householdName}" to confirm`}
                htmlFor="confirmDelete"
              >
                <TextInput
                  id="confirmDelete"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={settings.householdName}
                  disabled={saving}
                  autoComplete="off"
                />
              </FormGroup>
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => setShowDeleteModal(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn-danger" 
                onClick={handleDeleteHousehold}
                disabled={saving || deleteConfirmText !== settings.householdName}
              >
                {saving ? 'Deleting...' : 'Delete Household'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HouseholdSettings;
